import type { StudentPoints, StudentPointsCourse } from '@/api/progress';

/**
 * Баллы ученика у сотрудника: разбор ответа без разметки (договор
 * `docs/CONTRACTS/student-points.md`).
 *
 * Логика вынесена из компонента, потому что ломается тихо: неверная нарезка
 * списка не покажет ошибку, она просто оставит часть учеников без чисел — то
 * самое недоверие к цифрам, ради которого задача и делалась.
 */

/**
 * Предел пакетной ручки. Больше — сервер отвечает 400, а не режет молча: усечённый
 * список показал бы части учеников ноль вместо настоящих баллов (договор §3).
 */
export const MAX_POINTS_IDS = 500;

/**
 * Нарезка списка учеников на пачки под пакетную ручку.
 *
 * ⚠️ БЕЗ НЕЁ СПИСОК КУРАТОРА ОСТАЁТСЯ ВОВСЕ БЕЗ ЧИСЕЛ. Экран `/curator/students`
 * грузит всех учеников одним запросом, пагинации там нет; в PRE-UPPER это 554
 * строки, у куратора бывает 850. Один запрос на 850 id — 400 на весь список.
 *
 * Пустые значения и повторы выбрасываются: ответ всё равно карта «id → баллы»,
 * а повтор занимал бы место в пачке.
 */
export function chunkIds(ids: string[], size: number = MAX_POINTS_IDS): string[][] {
  if (size < 1) return [];
  const seen = new Set<string>();
  const clean: string[] = [];
  for (const raw of ids) {
    const id = (raw ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    clean.push(id);
  }
  const out: string[][] = [];
  for (let i = 0; i < clean.length; i += size) out.push(clean.slice(i, i + size));
  return out;
}

/** Строка разбивки: курс либо «вне курсов». */
export type PointsRow =
  | { kind: 'course'; id: string; title: string; points: number; deleted: boolean }
  | { kind: 'outside'; id: 'outside'; points: number };

/**
 * Строки «курс — баллы» плюс «вне курсов».
 *
 * ⚠️ СТРОКА «ВНЕ КУРСОВ» ОБЯЗАТЕЛЬНА, КОГДА ЧИСЛО НЕ НОЛЬ. Представление относит
 * к курсу только пройденные уроки и проверенную домашку, поэтому сумма по курсам
 * меньше общей. Без этой строки куратор сложит курсы, не получит общую сумму и
 * решит, что баллы пропали, — мы сменили бы одно недоверие к числам другим.
 *
 * При нуле строки нет: «вне курсов — 0 баллов» ничего не объясняет и только
 * занимает место.
 *
 * Порядок курсов не меняем — сервер уже отсортировал по убыванию баллов.
 */
export function pointsRows(data: StudentPoints | undefined | null): PointsRow[] {
  if (!data) return [];
  const rows: PointsRow[] = (data.courses ?? []).map((c: StudentPointsCourse) => ({
    kind: 'course' as const,
    id: c.marathon_id,
    title: c.title ?? '',
    points: c.points,
    deleted: !!c.deleted,
  }));
  if (data.outside_courses > 0) rows.push({ kind: 'outside', id: 'outside', points: data.outside_courses });
  return rows;
}

/**
 * Сходится ли «сумма по курсам + вне курсов» с общей суммой.
 *
 * Договор эту арифметику гарантирует и стережёт проверкой на живой базе, поэтому
 * на экране число не пересчитывается: показывается `total_points` сервера.
 * Здесь равенство нужно сторожу — разойтись оно может только если мы сами
 * начнём фильтровать строки разбивки.
 */
export function pointsAddUp(data: StudentPoints): boolean {
  const inCourses = (data.courses ?? []).reduce((sum, c) => sum + c.points, 0);
  return inCourses + data.outside_courses === data.total_points;
}
